import type { SagaIterator } from '@redux-saga/types';
import Immutable, { isImmutable } from 'immutable';
import { isAbbreviatioinsListLayout } from 'modules/Abbreviations/utils/isAbbreviatioinsListLayout';
import { AnyAction } from 'redux-saga';
import { call } from 'redux-saga/effects';

import { isEqual } from 'components/ArtboardAssets/utils/stack';
import { EntityType, LATEST_REUSABLE_LAYOUT_VERSION, Layer } from 'const';
import { updateDocument } from 'containers/Documents/actions';
import { documents as documentsSelector } from 'containers/Documents/selectors';
import { layeredLayouts } from 'containers/Layouts/selectors';
import { getLayoutAssetsByLayoutId } from 'containers/Layouts/services/getLayoutAssetsByLayoutId';
import * as Models from 'models';
import { isReferenceCitationElement } from 'utils/layouts/isReferenceCitationElement';
import { isSpacerElement } from 'utils/layouts/isSpacerElement';
import { prioritizeActiveLayerInLayeredRelations } from 'utils/prioritizeActiveLayerInLayeredRelations';
import { isColumnRelation } from 'utils/relations/isColumnRelation';
import { isMockReusableLayout } from 'utils/reusableLayouts/isMockReusableLayout';
import { isReusableLayout } from 'utils/reusableLayouts/isReusableLayout';
import { updateLayeredRelations } from '../actions';

// Track which indices have been modified for each relation to prevent overwriting
const modifiedIndicesByRelation: Record<string, Set<number>> = {};

// Global state for relations by layout ID
const relationsByLayoutId: Record<string, Models.LayeredRelationsMap> = {};

export { modifiedIndicesByRelation, relationsByLayoutId };

export function mergeRowsHeight(
  oldRowsHeight: Immutable.List<number>,
  newRowsHeight: Immutable.List<number>,
  relationId: string,
): Immutable.List<number> {
  const modifiedIndices = modifiedIndicesByRelation[relationId];

  // Create a new list based on the old one, but override with new values
  return oldRowsHeight.map((oldValue, index) => {
    const newValue = newRowsHeight.get(index);

    // Only update if this index hasn't been modified before
    if (!modifiedIndices.has(index) && newValue !== undefined && !isEqual(newValue, oldValue)) {
      modifiedIndices.add(index);

      return newValue;
    }

    return oldValue;
  });
}

export function mergeColumnRelationProperties(
  oldProperty: Models.ColumnRelationStylesMap,
  newProperty: Models.ColumnRelationStylesMap,
  relationId: string,
): Models.ColumnRelationStylesMap {
  // In case of change of heights, happening simultaneously across multiple relations,
  // we want to have keep change from every relation, and not override what was changed already on each iteration
  if (isImmutable(oldProperty) && oldProperty?.get('rowsHeight') && !isEqual(oldProperty, newProperty)) {
    const oldRowsHeight = oldProperty.get('rowsHeight');
    const newRowsHeight = newProperty.get('rowsHeight');

    if (Immutable.isList(oldRowsHeight) && Immutable.isList(newRowsHeight)) {
      const mergedList = mergeRowsHeight(
        oldRowsHeight as Immutable.List<number>,
        newRowsHeight as Immutable.List<number>,
        relationId,
      );

      return newProperty.set('rowsHeight', mergedList);
    }

    return newProperty;
  }

  return oldProperty;
}

export function mergeLayeredRelations(
  existingRelation: Models.LayeredRelationMap,
  newRelation: Models.LayeredRelationMap,
): Models.LayeredRelationMap {
  // standard logic of merge
  if (!existingRelation) {
    return newRelation;
  }
  if (!newRelation) {
    return existingRelation;
  }

  const existingId = existingRelation.getIn(['id']);
  const newId = newRelation.getIn(['id']);

  // Check if this is a column relation
  if (existingId === newId && isColumnRelation(existingRelation)) {
    const relationId = existingId as string;

    // Initialize tracking for this relation if it doesn't exist
    if (!modifiedIndicesByRelation[relationId]) {
      modifiedIndicesByRelation[relationId] = new Set<number>();
    }

    return existingRelation.mergeWith(
      (oldProperty: Models.ColumnRelationStylesMap, newProperty: Models.ColumnRelationStylesMap) =>
        mergeColumnRelationProperties(oldProperty, newProperty, relationId),
      newRelation as Models.ColumnRelationMap,
    );
  }

  // For non-column relations, use the new relation (existing behavior)
  return newRelation;
}

export function collectRelationIdsForCleanup(
  batchedRelations: Models.LayeredRelationsMap,
  relationIdsToCleanup: string[],
): void {
  batchedRelations.forEach((relation) => {
    const relationId = relation?.getIn(['id']) as string;
    if (relationId && modifiedIndicesByRelation[relationId]) {
      relationIdsToCleanup.push(relationId);
    }
  });
}

export function* handleReusableLayout(
  layout: Models.LayeredLayoutMap | Models.LayeredGroupLayoutMap,
  batchedRelations: Models.LayeredRelationsMap,
  layoutId: string,
  activeLayer: Layer,
): SagaIterator<AnyAction> {
  const { [layoutId]: layoutAssets }: ReturnTypeSaga<typeof getLayoutAssetsByLayoutId> = yield call(
    getLayoutAssetsByLayoutId,
    [layoutId],
  );
  const prioritizedRelations = prioritizeActiveLayerInLayeredRelations(batchedRelations, activeLayer);
  const updatedLayoutAssets = (layoutAssets as Models.LayoutAssetsMap).set('relations', prioritizedRelations);
  const updatedEntities: PlainObjectWithImmutableProps<Models.ReusableLayoutEntities> = {
    brandDefinition: updatedLayoutAssets.get('brandDefinition'),
    relationId: updatedLayoutAssets.get('relationId'),
    relations: updatedLayoutAssets.get('relations'),
    styles: updatedLayoutAssets.get('styles'),
    version: LATEST_REUSABLE_LAYOUT_VERSION,
    layoutSourceBrandDefinition: updatedLayoutAssets.get('layoutSourceBrandDefinition'),
  };

  return updateDocument({
    id: layout.getIn(['documentId', activeLayer]),
    documents: updatedLayoutAssets.get('documents') as unknown as Models.CombinedDocuments,
    entities: Immutable.Map(updatedEntities) as unknown as Models.ReusableLayoutEntities,
    entityType: EntityType.LAYOUT,
  });
}

export function* processLayoutBatch(
  layoutIdsToBeProcessed: Immutable.List<string>,
  layouts: ReturnTypeSaga<typeof layeredLayouts>,
  activeLayer: Layer,
  documents: ReturnTypeSaga<typeof documentsSelector>,
): SagaIterator<{ actions: AnyAction[]; relationIdsToCleanup: string[] }> {
  const actions: AnyAction[] = [];
  const relationIdsToCleanup: string[] = [];

  for (let i = 0; i <= layoutIdsToBeProcessed.size - 1; i++) {
    const layoutIdForProcessing = layoutIdsToBeProcessed.get(i) ?? '';
    const batchedRelations = relationsByLayoutId[layoutIdForProcessing];

    if (!batchedRelations) {
      continue;
    }

    const layout = layouts.get(layoutIdForProcessing);
    const callToAction = batchedRelations.find(item => item.getIn(['entityType']) === 'CallToAction');

    if (activeLayer === Layer.TRANSLATED && callToAction && !callToAction.getIn(['styles', Layer.TRANSLATED])) {
      continue;
    }

    actions.push(updateLayeredRelations(batchedRelations));
    collectRelationIdsForCleanup(batchedRelations, relationIdsToCleanup);

    if (isReusableLayout(layout, documents, activeLayer) || isMockReusableLayout(layout, documents, activeLayer)) {
      const reusableLayoutAction: AnyAction = yield call(
        handleReusableLayout,
        layout,
        batchedRelations,
        layoutIdForProcessing,
        activeLayer,
      );
      actions.push(reusableLayoutAction);
    }
  }

  return { actions, relationIdsToCleanup };
}

export function getProcessableLayoutIds(
  flattenedLayouts: Models.CombinedLayoutsMap | undefined,
  layouts: ReturnTypeSaga<typeof layeredLayouts>,
  layoutId: string,
): Immutable.List<string> | undefined {
  const layoutIds = flattenedLayouts?.keySeq().toList();

  let layoutIdsToBeProcessed = layoutIds?.filter((currentLayoutId) => {
    const layout = layouts.get(currentLayoutId) as Models.LayeredLayoutMap;

    return !(isSpacerElement(layout) || isReferenceCitationElement(layout) || isAbbreviatioinsListLayout(layout));
  });

  // in case all layouts already have heights, only current layout will be updated
  if (layoutIdsToBeProcessed?.size === 0) {
    layoutIdsToBeProcessed = Immutable.List([layoutId]);
  }

  return layoutIdsToBeProcessed;
}
